import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ShieldCheck, Zap, Search, Star, Award, ChevronRight, 
  ArrowRight, CheckCircle2, TrendingUp, Sparkles, HelpCircle, FileText 
} from 'lucide-react';
import { Game, Listing } from '../types';
import { GameCard } from '../components/game/GameCard';
import { ListingCard } from '../components/listing/ListingCard';
import { BoostingCalculator } from '../components/boosting/BoostingCalculator';

export const Home: React.FC = () => {
  const navigate = useNavigate();
  const [games, setGames] = useState<Game[]>([]);
  const [featuredListings, setFeaturedListings] = useState<Listing[]>([]);
  const [heroSearch, setHeroSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [gamesRes, listingsRes] = await Promise.all([
          fetch('/api/games?sort=popular'),
          fetch('/api/listings?sort=featured&limit=8')
        ]);
        if (gamesRes.ok) {
          const gData = await gamesRes.json();
          if (gData.success) setGames(gData.games || []);
        }
        if (listingsRes.ok) {
          const lData = await listingsRes.json();
          if (lData.success) setFeaturedListings(lData.listings || []);
        }
      } catch (err) {
        console.error('Failed to load home data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleHeroSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (heroSearch.trim()) {
      navigate(`/browse?q=${encodeURIComponent(heroSearch.trim())}`);
    }
  };

  return (
    <div className="space-y-16 pb-16">
      
      {/* 1. Hero Section */}
      <section className="relative overflow-hidden pt-12 pb-20 bg-gradient-to-b from-brand-lightSec via-white to-white dark:from-brand-darkCard/60 dark:via-brand-darkBg dark:to-brand-darkBg border-b border-brand-lightBorder dark:border-brand-darkBorder">
        {/* Glow ambient background circles */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-brand-blue/10 dark:bg-brand-accent/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-brand-blue/10 dark:bg-brand-accent/15 text-brand-blue dark:text-brand-accent border border-brand-blue/20">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Next-Gen Gaming Commerce Platform</span>
          </div>

          <h1 className="text-4xl sm:text-6xl font-black tracking-tight text-brand-lightText dark:text-brand-darkText max-w-4xl mx-auto leading-[1.15]">
            Level Up Your Game With <br className="hidden sm:inline" />
            <span className="bg-gradient-to-r from-brand-blue via-indigo-500 to-brand-accent bg-clip-text text-transparent">
              Verified Gaming Assets
            </span>
          </h1>

          <p className="text-sm sm:text-base text-brand-lightSecText dark:text-brand-darkSecText max-w-2xl mx-auto leading-relaxed">
            The trustworthy marketplace for high-tier accounts, rank boosting, in-game currencies, and global keys. Protected by automated AES-GCM delivery and 72-hour buyer warranty.
          </p>

          {/* Hero Search Box */}
          <form onSubmit={handleHeroSearch} className="max-w-2xl mx-auto relative pt-4">
            <div className="relative flex items-center shadow-lg rounded-2xl overflow-hidden border border-brand-lightBorder dark:border-brand-darkBorder bg-white dark:bg-brand-darkCard focus-within:ring-2 focus-within:ring-brand-blue">
              <Search className="w-5 h-5 text-brand-lightSecText ml-4" />
              <input
                type="text"
                value={heroSearch}
                onChange={(e) => setHeroSearch(e.target.value)}
                placeholder="Search games, ranks, skins, accounts, or services..."
                className="w-full px-4 py-4 text-sm bg-transparent text-brand-lightText dark:text-brand-darkText placeholder-brand-lightSecText focus:outline-none"
              />
              <button
                type="submit"
                className="mr-2 px-6 py-2.5 rounded-xl font-bold text-xs text-white bg-brand-blue hover:bg-brand-hover shadow-glow-blue transition-all"
              >
                Search
              </button>
            </div>
          </form>

          {/* Popular Category Chips */}
          <div className="pt-2 flex flex-wrap items-center justify-center gap-2 text-xs font-semibold text-brand-lightSecText dark:text-brand-darkSecText">
            <span>Trending:</span>
            {[
              { label: 'Valorant Accounts', path: '/browse?game=valorant&category=accounts' },
              { label: 'LoL Boosting', path: '/boosting' },
              { label: 'CS2 Skins', path: '/browse?game=counter-strike-2&category=items' },
              { label: 'WoW Gold', path: '/browse?game=world-of-warcraft&category=currency' },
              { label: 'Elden Ring Keys', path: '/browse?game=elden-ring&category=keys' },
            ].map((chip) => (
              <Link
                key={chip.label}
                to={chip.path}
                className="px-3 py-1 rounded-full bg-brand-lightSec dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder text-brand-lightText dark:text-brand-darkText hover:border-brand-blue transition-colors"
              >
                {chip.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 2. Popular Games Carousel / Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-black text-brand-lightText dark:text-brand-darkText flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-brand-blue dark:text-brand-accent" />
              <span>Popular Games</span>
            </h2>
            <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText mt-1">
              Browse top titles with thousands of verified offers
            </p>
          </div>
          <Link
            to="/games"
            className="text-xs font-bold text-brand-blue dark:text-brand-accent hover:underline flex items-center gap-1"
          >
            <span>View All Games</span>
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {games.slice(0, 8).map((game) => (
            <GameCard key={game.id} game={game} />
          ))}
        </div>
      </section>

      {/* 3. Featured Verified Listings */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-black text-brand-lightText dark:text-brand-darkText flex items-center gap-2">
              <Award className="w-6 h-6 text-brand-blue dark:text-brand-accent" />
              <span>Featured Listings</span>
            </h2>
            <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText mt-1">
              Hand-picked verified deals from top-rated sellers
            </p>
          </div>
          <Link
            to="/browse"
            className="text-xs font-bold text-brand-blue dark:text-brand-accent hover:underline flex items-center gap-1"
          >
            <span>Browse All Offers</span>
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featuredListings.map((listing) => (
            <ListingCard key={listing.id} listing={listing} />
          ))}
        </div>
      </section>

      {/* 4. Interactive Boosting Configurator Highlight */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-500/10 text-amber-500 border border-amber-500/20 mb-2">
            <Zap className="w-3.5 h-3.5" />
            <span>Interactive Rank Calculator</span>
          </div>
          <h2 className="text-2xl font-black text-brand-lightText dark:text-brand-darkText">
            Professional Rank Boosting & Coaching
          </h2>
          <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText mt-1">
            Choose your current and desired rank for instant server-calculated pricing and completion estimates.
          </p>
        </div>

        <BoostingCalculator initialGame="valorant" />
      </section>

      {/* 5. How Purchasing Works */}
      <section className="bg-brand-lightSec/60 dark:bg-brand-darkCard/40 py-16 border-y border-brand-lightBorder dark:border-brand-darkBorder">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl font-black text-brand-lightText dark:text-brand-darkText">
              How GameVault Works
            </h2>
            <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText mt-1">
              A secure, automated marketplace designed for fast and protected transactions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-6 rounded-2xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder shadow-sm relative">
              <span className="w-8 h-8 rounded-full bg-brand-blue text-white font-black text-sm flex items-center justify-center mb-4 shadow-glow-blue">
                1
              </span>
              <h3 className="font-bold text-base text-brand-lightText dark:text-brand-darkText mb-2">
                Choose & Validate
              </h3>
              <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText leading-relaxed">
                Filter verified accounts, boosting services, or game keys with dynamic game-specific attributes, transparent ratings, and clear warranties.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder shadow-sm relative">
              <span className="w-8 h-8 rounded-full bg-brand-blue text-white font-black text-sm flex items-center justify-center mb-4 shadow-glow-blue">
                2
              </span>
              <h3 className="font-bold text-base text-brand-lightText dark:text-brand-darkText mb-2">
                Protected Checkout
              </h3>
              <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText leading-relaxed">
                Atomic stock allocation guarantees keys cannot be double-sold. Funds are securely held while you inspect and verify your purchase.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder shadow-sm relative">
              <span className="w-8 h-8 rounded-full bg-brand-blue text-white font-black text-sm flex items-center justify-center mb-4 shadow-glow-blue">
                3
              </span>
              <h3 className="font-bold text-base text-brand-lightText dark:text-brand-darkText mb-2">
                Instant Delivery & Warranty
              </h3>
              <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText leading-relaxed">
                Receive AES-GCM decrypted keys instantly or coordinate with your booster. Enjoy full 72-hour warranty protection with staff dispute resolution.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Buyer Protection Callout Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl p-8 sm:p-12 bg-gradient-to-r from-brand-blue to-indigo-700 text-white flex flex-col md:flex-row items-center justify-between gap-8 shadow-2xl">
          <div className="space-y-3 max-w-xl">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-white/20 backdrop-blur-md">
              <ShieldCheck className="w-4 h-4" />
              <span>GameVault Shield Active</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-black">
              100% Guaranteed Buyer Protection
            </h3>
            <p className="text-xs sm:text-sm text-white/80 leading-relaxed">
              Every purchase on GameVault is backed by our strict seller identity verification and 72-hour warranty. If an account or key has an issue, receive an immediate replacement or full store credit.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 flex-shrink-0">
            <Link
              to="/legal?tab=refund"
              className="px-6 py-3 rounded-2xl text-xs font-bold text-brand-blue bg-white hover:bg-slate-100 shadow-lg transition-all"
            >
              Read Warranty Policy
            </Link>
            <Link
              to="/browse"
              className="px-6 py-3 rounded-2xl text-xs font-bold text-white bg-white/10 hover:bg-white/20 border border-white/30 backdrop-blur-md transition-all"
            >
              Start Shopping
            </Link>
          </div>
        </div>
      </section>

      {/* 7. Frequently Asked Questions */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-black text-brand-lightText dark:text-brand-darkText flex items-center justify-center gap-2">
            <HelpCircle className="w-6 h-6 text-brand-blue dark:text-brand-accent" />
            <span>Frequently Asked Questions</span>
          </h2>
          <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText mt-1">
            Common questions about purchasing and security on GameVault
          </p>
        </div>

        <div className="space-y-4">
          {[
            {
              q: 'How does instant automatic delivery work?',
              a: 'Digital game keys and full-access account credentials are encrypted with AES-GCM before storage. When your payment is confirmed by the server, the unique key is atomically allocated and decrypted directly to your order screen.'
            },
            {
              q: 'What is the 72-Hour Buyer Warranty?',
              a: 'All eligible purchases carry a minimum 72-hour warranty. If a delivered account is recalled or credentials fail, you can open an immediate dispute from your order dashboard for staff mediation and refund.'
            },
            {
              q: 'How does Duo Queue Boosting protect my account?',
              a: 'With Duo Queue, you play alongside our verified booster from your own computer. You never share your login credentials or risk IP discrepancies.'
            },
            {
              q: 'What payment methods are supported in the sandbox?',
              a: 'For development and evaluation, GameVault provides an explicitly labeled Sandbox Provider that exercises the full server order lifecycle, atomic stock reservation, VaultPoints rewards, and dispute resolution workflows.'
            }
          ].map((faq, idx) => (
            <div key={idx} className="p-5 rounded-2xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder">
              <h4 className="font-bold text-sm text-brand-lightText dark:text-brand-darkText mb-1.5">{faq.q}</h4>
              <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
